import React, { ReactNode } from 'react';
import classNames from 'classnames';

import appActions from 'common/support/applicationActions';
import focusManager from 'support/focus/FocusManager';
import FocusGroup from 'components/focus/FocusGroup';
import withRestoration from 'components/withRestoration';
import { WithRestorationProps } from 'components/withRestoration';
import { GridState } from 'app-types';

import { getAppScalingFactor } from 'support/scalingHelper';

import styles from './Grid.module.scss';

// We need to manually calculate the scaling factor (app width / 1920) since certain measurements
// are not scaled appropriately even though the element is affected by transform: scale
// getBoundingClientRect values are scaled automatically, whereas offset (ie. offsetTop/offsetLeft)
// and buffers need to be scaled manually
const scaling = getAppScalingFactor();

export interface GridDataItem {
  id: string | number;
  [key: string]: unknown;
}

export interface GridOwnProps {
  numCols: number;
  renderItem: (item: GridDataItem, index: number) => ReactNode;
  data: GridDataItem[];
  prevBuffer: number;
  nextBuffer: number;
  className?: string | null;
  focusId?: string | number | null;
  restorationId?: string;
}

type GridProps = GridOwnProps & WithRestorationProps;

class Grid extends React.Component<GridProps, GridState> {
  static defaultProps: Partial<GridOwnProps> = {
    className: null,
    focusId: null,
  };

  gridViewport = React.createRef<HTMLDivElement>();

  firstRow = React.createRef<HTMLDivElement>();

  focusGroup = React.createRef<FocusGroup>();

  focusedIndex = 0;

  timeout: ReturnType<typeof setTimeout> | null = null;

  rowHeight: number | undefined | null = null;

  focusId: string | number | null = null;

  constructor(props: GridProps) {
    const { restorationState } = props;

    super(props);

    this.focusId = props.focusId || focusManager.getNewUniqueId();

    if (restorationState) {
      this.state = restorationState;
    } else {
      this.state = {
        isAnimating: false,
        top: 0,
        endIndex: this.getEndIndex(0),
        startIndex: this.getStartIndex(0),
      };
    }
  }

  componentDidMount(): void {
    const { data } = this.props;
    focusManager.addFocusChangedListener(this.onFocusChanged);
    if (data && data.length) {
      this.rowHeight = this.firstRow?.current?.getBoundingClientRect().height;
    }
  }

  componentDidUpdate(prevProps: GridProps, prevState: GridState): void {
    const { data, setRestorationState } = this.props;

    if (!prevProps.data && data && data.length) {
      this.rowHeight = this?.firstRow?.current?.getBoundingClientRect().height;
    }

    if (prevState !== this.state) {

      setRestorationState(this.state);
    }
  }

  componentWillUnmount(): void {
    focusManager.removeFocusChangedListener(this.onFocusChanged);
  }

  onFocusChanged = (): void => {

    if (!window || !focusManager.isFocusAncestor(this.focusId)) return;

    const focusedEl = focusManager.getFocusableElement(

      focusManager.currentFocusId!,
    );

    const {
      top: rectTop,
      bottom: rectBottom,
    } = focusedEl?.getBoundingClientRect() ?? {};

    const {
      top: viewportTop,
      bottom: viewportBottom,
    } = this.gridViewport?.current?.getBoundingClientRect() ?? {};

    const offsetTop = (focusedEl as HTMLElement).offsetTop * scaling;
    const viewportHeight = (viewportBottom as number) - (viewportTop as number);
    const itemHeight = (rectBottom as number) - (rectTop as number);

    if ((rectTop as number) < (viewportTop as number)) {
      this.setState(
        {
          isAnimating: true,
          top: Math.min(0, -offsetTop + 50 * scaling),
        },
        () => {
          if (!this.timeout) {
            this.timeout = setTimeout(() => {
              this.timeout = null;
              this.handleClipping();
            }, 300);
          }
        },
      );
    } else if ((rectBottom as number) > (viewportBottom as number)) {
      this.setState(
        {
          isAnimating: true,
          top: -offsetTop + viewportHeight - itemHeight - 50 * scaling,
        },
        () => {
          if (!this.timeout) {
            this.timeout = setTimeout(() => {
              this.timeout = null;
              this.handleClipping();
            }, 300);
          }
        },
      );
    } else {
      this.handleClipping();
    }
  };

  getStartIndex(focusedIndex: number): number {
    const { prevBuffer, numCols } = this.props;
    return (
      Math.floor(Math.max(0, focusedIndex - prevBuffer) / numCols) * numCols
    );
  }

  getEndIndex(focusedIndex: number): number {
    const { nextBuffer, prevBuffer, numCols } = this.props;

    return (
      this.getStartIndex(focusedIndex) +
      Math.ceil((nextBuffer + prevBuffer) / numCols) * numCols
    );
  }

  onFocusIndexOverride = (action: string, currentIndex: number): number | 'handled' | false => {
    const { numCols, data } = this.props;
    const { startIndex } = this.state;

    let newIndex: number | false = false;
    switch (action) {
      case appActions.moveUp:
        newIndex = currentIndex - numCols;
        break;
      case appActions.moveDown:
        if (currentIndex + numCols <= data.length - 1 - (startIndex as number)) {
          newIndex = currentIndex + numCols;
          break;
        }

        // Only want to push the index to the upper bound if the upper
        // bound is on the next row.  Otherwise, we don't want to handle
        // the action
        newIndex =
          Math.floor((data.length - 1 - (startIndex as number)) / numCols) >
          Math.floor(currentIndex / numCols)
            ? data.length - 1 - (startIndex as number)
            : false;
        break;
      case appActions.moveLeft:
        // if first item in row then return currentIndex
        if (currentIndex % numCols === 0) {
          newIndex = currentIndex;
          focusManager.setCurrentFocus('left-nav-focusable-content');
        }
        break;
      case appActions.moveRight:
        // if last item in row then return currentIndex
        if (currentIndex % numCols === numCols - 1) {
          newIndex = currentIndex;
        }
        break;
      default:
        newIndex = false;
    }

    if (action !== appActions.moveUp && action !== appActions.moveDown) {
      return newIndex;
    }

    const {
      top: viewportTop,
      bottom: viewportBottom,

    } = this.gridViewport.current?.getBoundingClientRect() ?? {};

    const gridFocusChildren = focusManager.getFocusableChildren(this.focusId);
    const newChild = gridFocusChildren?.[newIndex as number];

    if (newChild) {
      const {
        top: newFocusTop,
        bottom: newFocusBottom,
      } = newChild.getBoundingClientRect();

      if (
        this.timeout &&
        (newFocusTop < (viewportTop as number) || newFocusBottom > (viewportBottom as number))
      ) {
        return 'handled';
      }
    }

    return newIndex;
  };

  onFocusIndexChanged = (index: string | number): void => {
    this.focusedIndex = index as number;
  };

  splitData = (arr: GridDataItem[]): GridDataItem[][] => {
    const { numCols } = this.props;
    return arr.reduce<GridDataItem[][]>((accumulator, value, index) => {
      const accumulatorIndex = Math.floor(index / numCols);
      if (!accumulator[accumulatorIndex]) {
        accumulator.push([]);
      }
      accumulator[accumulatorIndex].push(value);
      return accumulator;
    }, []);
  };

  handleClipping = (): void => {
    const { numCols } = this.props;
    const { startIndex, endIndex, top } = this.state;

    // Need to add these indexes together since the FocusGroup is only
    // receiving a subset of items
    const focusedIndex = this.focusedIndex + (startIndex as number);
    const newStartIndex = this.getStartIndex(focusedIndex);
    const newEndIndex = this.getEndIndex(focusedIndex);

    if (
      (newStartIndex !== startIndex || newEndIndex !== endIndex) &&
      this.rowHeight
    ) {
      const amountToClip = Math.abs(newStartIndex - (startIndex as number)) / numCols;
      this.setState(
        {
          isAnimating: false,
          startIndex: newStartIndex,
          endIndex: newEndIndex,
          top:
            newStartIndex > (startIndex as number)
              ? (top as number) + this.rowHeight * amountToClip
              : (top as number) - this.rowHeight * amountToClip,
        },
        () => {
          if (this.focusGroup && this.focusGroup.current) {
            this.focusGroup.current.resetFocusIndex();
          }
        },
      );
    }
  };

  render(): ReactNode {
    const { numCols, renderItem, data, className } = this.props;
    const { top, startIndex, endIndex, isAnimating } = this.state;

    const splitData = this.splitData(data);
    return (

      <div className={className ?? undefined} ref={this.gridViewport}>
        <FocusGroup
          className={classNames(
            'flex-wrap',
            'h-100',
            'w-100',
            'align-content-start',
            'justify-content-center',
            {
              [styles.animate]: isAnimating,
            },
            styles.grid,
          )}

          focusId={this.focusId ? this.focusId.toString() : undefined}
          onFocusIndexOverride={this.onFocusIndexOverride}
          onFocusIndexChanged={this.onFocusIndexChanged}
          ref={this.focusGroup}
          style={{
            transform: `translateY(${(top as number) / scaling}px)`,
            WebkitTransform: `translateY(${(top as number) / scaling}px)`,
          }}
          horizontal
        >
          {splitData
            .slice((startIndex as number) / numCols, (endIndex as number) / numCols + 1)
            .map((row, rowIndex) => (
              <div
                className={classNames(
                  'grid-row d-flex justify-content-start w-100',
                  styles.gridrow,
                )}
                ref={rowIndex === 0 ? this.firstRow : undefined}
                key={row[0].id}
              >
                {row.map((item, index) =>
                  renderItem(item, index + rowIndex * numCols + (startIndex as number)),
                )}
              </div>
            ))}
        </FocusGroup>
      </div>
    );
  }
}

export default withRestoration<GridOwnProps & Record<string, unknown>>(props => props.restorationId)(Grid);
